<script setup lang="ts">
    import { computed, ref, watch, watchEffect } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { UploadedFile } from '../Form/Composable/file-upload';
    import { useTheme } from '../../Composables/theme';

    const props = withDefaults(defineProps<{
        name?: string,
        title: string,
        replaceTitle: string;
        maxFiles?: number,
        disabled: boolean,
        accept?: string[],
        files: UploadedFile[]
    }>(), {
        name: 'file',
        title: '',
        maxFiles: 1,
        disabled: false,
        accept: () => []
    });

    const emit = defineEmits<{
        'files-selected': [File[]];
    }>();

    const { t } = useI18n();
    const { theme } = useTheme();

    const inputId = computed(() => `file-input-${props.name}`);

    const fileInput = ref();
    const errorDisplayTime = 5000;
    const unsupportedFiles = ref<File[]>([]);
    const errorTimeout = ref<number|null>(null);

    const addFiles = (newFiles: File[]) => {
        const [acceptedFiles, rejectedFiles] = newFiles.reduce((destination, f) => {
            destination[(!props.accept.length || props.accept.includes(f.type)) ? 0 : 1].push(f);
            return destination;
        }, [[], []] as [File[], File[]]);
        emit('files-selected', acceptedFiles.slice(0, props.maxFiles));
        unsupportedFiles.value = rejectedFiles;
        removeFileErrorsAfterDelay();
    };

    const removeFileErrorsAfterDelay = () => {
        if (errorTimeout.value) {
            window.clearTimeout(errorTimeout.value);
        }
        errorTimeout.value = window.setTimeout(() => {
            unsupportedFiles.value = [];
        }, errorDisplayTime);
    };

    const dropFiles = (event: DragEvent) => {
        if (!props.disabled) {
            if (event.dataTransfer?.files) {
                addFiles([...event.dataTransfer.files]);
            }
            setActive(false);
        }
    };

    const onFileChange = (event: Event) => {
        if ((event.target as HTMLInputElement)?.files) {
            addFiles([...(event.target as HTMLInputElement).files!]);
        }
    };

    watch(() => props.files, (newFiles) => {
        if (!newFiles.length) {
            fileInput.value.value = "";
        }
    });

    const removeFile = (fileIndex: number) => {
        if (!props.disabled) {
            const updatedFiles = Array.from(props.files);
            updatedFiles.splice(fileIndex, 1);
            emit('files-selected', updatedFiles.map((f) => f.file));
        }
    };

    const active = ref(false);
    const setActive = (value: boolean) => {
        if (!props.disabled) {
            active.value = value;
        }
    };

    const canDisplayPreview = (file: UploadedFile) => {
        return file.url && file.url.split('.').pop()?.match(/(jpg|jpeg|png|gif|svg)/i);
    };
</script>
<template>
    <label
        @dragenter.prevent="() => setActive(true)"
        @dragleave.prevent="() => setActive(false)"
        @dragover.prevent
        @drop.prevent="dropFiles"
        :class="['upload-card', {active}, {selected: files.length}, {disabled}, theme]" :for="inputId"
    >
        <div class="card-content">
            <input
                :id="inputId"
                class="file-input"
                type="file"
                @change="onFileChange"
                :multiple="props.maxFiles > 1"
                :disabled="props.disabled"
                :accept="accept.join(',')"
                ref="fileInput"
                data-testid="upload-card-file-input"
            />
            <TransitionGroup tag="div" name="file" :class="['files-list', {displayed: files.length || unsupportedFiles.length}]">
                <div v-for="(file, i) in files" :key="i" class="file-entry">
                    <span
                        v-if="file.progress?.progress"
                        :class="[
                            'upload-progress',
                            {done: file.progress.progress >= 1},
                            {uploading: file.progress.progress < 1}
                        ]"
                        :style="{width: (file.progress.progress * 100) + '%'}"
                    ></span>
                    <div class="file-preview" v-if="canDisplayPreview(file)">
                        <img :src="file.url" alt="file preview"/>
                    </div>
                    <p class="file-name">{{ file.file.name }}</p>
                    <div class="remove-file" @click.prevent.stop="() => removeFile(i)">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="8" cy="8" r="8" class="circle-background"/>
                            <path d="M11 11L5 5" class="cross-stroke" stroke-linecap="round"/>
                            <path d="M5 11L11 5" class="cross-stroke" stroke-linecap="round"/>
                        </svg>
                    </div>
                </div>
                <div v-for="(file, i) in unsupportedFiles" :key="'invalid-' + i" class="file-entry invalid">
                    <p class="file-name"><span class="unsupported">{{ t('upload.unsupported_format') }}</span>{{ file.name }}</p>
                </div>
            </TransitionGroup>
            <Transition name="fade">
                <div class="file-icon" v-if="!files.length && !unsupportedFiles.length && $slots.default">
                    <slot/>
                </div>
            </Transition>
            <p :class="['upload-title', {selected: files.length}]" v-if="props.title">
                {{ (files.length) ? props.replaceTitle : props.title }}
            </p>
        </div>
    </label>
</template>
<style lang="scss">
@import '@Common/Styles/variables';

@keyframes invalid-shake {
    10%, 90% { transform: translate3d(-1px, 0, 0); }
    20%, 80% { transform: translate3d(2px, 0, 0); }
    30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
    40%, 60% { transform: translate3d(4px, 0, 0); }
}

.upload-card {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    border: 2px solid var(--gray-300, map-get($yapla-gray, 300));
    border-style: dashed;
    border-radius: 6px;
    transition: all ease 0.5s;
    padding: 20px;
    width: 100%;
    min-width: 0;

    &.disabled {
        cursor: not-allowed;
        pointer-events: none;
        opacity: 0.5;
    }

    &.active:not(.selected), &:hover:not(.selected) {
        border-color: map-get($yapla-blue, 500);
        box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
        background-color: var(--gray-100, map-get($yapla-blue, 100));

        .card-content .upload-title {
            color: var(--gray-700, map-get($yapla-blue, 700));
        }
    }

    &.selected {
        border-color: map-get($yapla-blue, 500);
        border-style: solid;
    }

    &.cms {
        &.active:not(.selected), &:hover:not(.selected), &.selected {
            border-color: var(--primary);
        }
    }

    .card-content {
        display: flex;
        height: 100%;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        width: 100%;

        .upload-title {
            text-align: center;
            font-weight: bold;
            transition: color ease 0.2s, background-color ease 0.2s;
            color: var(--gray-700, map-get($yapla-gray, 700));
            padding: 2px 5px;
            border-radius: 6px;
            margin: 0;

            &.selected {
                color: var(--gray-600, map-get($yapla-gray, 600));
            }
        }

        .files-list {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 6px;
            justify-content: center;
            flex-grow: 1;
            position: relative;

            &.displayed {
                padding-bottom: 10px;
            }

            .file-entry {
                display: flex;
                width: 100%;
                justify-content: center;
                align-items: center;
                gap: 10px;
                padding: 6px;
                background-color: var(--gray-100, map-get($yapla-blue, 100));
                border-radius: 6px;
                position: relative;

                .upload-progress {
                    position: absolute;
                    will-change: width;
                    border-radius: 6px;
                    left: 0;
                    bottom: 0;
                    top: 0;
                    background-color: var(--gray-200, map-get($yapla-blue, 200));
                    opacity: 1;
                    transition: width 0.2s ease, opacity 0.2s ease;

                    &.done {
                        opacity: 0;
                    }
                }

                .file-preview {
                    border-radius: 3px;
                    overflow: hidden;

                    img {
                        width: 50px;
                        border-radius: 1px;
                    }
                }


                &.file-enter-active:not(.invalid) {
                    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
                }

                &.file-leave-active {
                    transition: all 0.5s cubic-bezier(0.7, 0, 0.84, 0);
                }

                &.file-enter,
                &.file-leave-to {
                    opacity: 0;
                    transform: translateY(30px);
                }

                &.invalid {
                    background-color: var(--red-200, map-get($yapla-red, 200));

                    &.file-enter-active {
                        animation: invalid-shake 0.5s;
                    }

                    .file-name {
                        .unsupported {
                            color: var(--red-800, map-get($yapla-red, 800));
                            font-weight: 600;
                            padding-right: 10px;
                        }
                        color: var(--red-500, map-get($yapla-red, 600));
                    }
                }

                .file-name {
                    flex-grow: 1;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    margin: 0;
                    line-height: 1.5em;
                    color: var(--gray-700, map-get($yapla-blue, 700));
                    z-index: 10;
                }

                .remove-file {
                    display: flex;
                    align-items: center;
                    flex-shrink: 1;
                    z-index: 10;

                    .circle-background {
                        fill: var(--gray-300, map-get($yapla-blue, 200));
                    }

                    .cross-stroke {
                        stroke: var(--gray-700, map-get($yapla-blue, 700));
                    }
                }
            }
        }

        .file-icon {
            padding-bottom: 10px;

            &.fade-enter-active {
                transition: all 0.3s ease 0.5s;
            }

            &.fade-enter,
            &.fade-leave-to {
                opacity: 0;
            }
        }
    }

    .file-input {
        display: none;
    }
}

</style>